import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Save } from 'lucide-react';
import { TeamProject } from '@/types/TeamProject';
import { toast } from '@/hooks/use-toast';

interface TeamProjectSettingsProps {
  project: TeamProject;
}

export function TeamProjectSettings({ project }: TeamProjectSettingsProps) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [hourlyRate, setHourlyRate] = useState(project.hourly_rate?.toString() || '75');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement after database migration
    toast({
      title: "Coming Soon",
      description: "Team project settings will be available after database setup.",
    });
  };

  const handleArchive = () => {
    if (confirm('Are you sure you want to archive this project? This action cannot be undone.')) {
      // TODO: Implement after database migration
      toast({
        title: "Coming Soon",
        description: "Team project archiving will be available after database setup.",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Project Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Project Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Project Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
              <Input
                id="hourlyRate"
                type="number"
                step="0.01"
                min="0"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              Save Changes
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Archive this project to hide it from active projects. This action can be undone from the archived projects list.
          </p>
          <Button 
            variant="destructive"
            onClick={handleArchive}
          >
            Archive Project
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}